import React from "react";
import StatusBadge from "./StatusBadge";
import { useWhatsAppAccountStatus } from "../../hooks/useWhatsAppAccountStatus";

const ONBOARDING_STEPS = [
  { key: "business_details", label: "Business Profile", required: true },
  { key: "services", label: "Services", required: true },
  { key: "business_config", label: "Setup & Customization", required: true },
  { key: "knowledge_base", label: "Knowledge Base", required: false },
  { key: "test", label: "Test", required: false },
  { key: "activate", label: "Activate", required: true },
];

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-t border-slate-100 py-3 first:border-t-0 first:pt-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="max-w-[60%] text-right text-sm font-medium text-slate-900">{value || "-"}</dd>
  </div>
);

// ─── WhatsApp Status Card ────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch { return iso; }
};

const CHECK_LABELS = {
  token_access: "Token Access",
  asset_access: "Asset Access",
  webhook_status: "Webhook",
  phone_registration_status: "Phone Registration",
};
const CHECK_PASS = new Set(["passed", "subscribed", "registered"]);

function WaCheckRow({ label, value }) {
  const passed = CHECK_PASS.has(String(value ?? "").toLowerCase());
  return (
    <div className="flex items-center justify-between gap-2 border-t border-slate-100 py-2 first:border-t-0 first:pt-0">
      <span className="flex items-center gap-1.5 text-xs text-slate-500">
        <span className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${passed ? "bg-emerald-400" : "bg-rose-400"}`} />
        {label}
      </span>
      <span className={`text-xs font-semibold ${passed ? "text-emerald-600" : "text-rose-500"}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function WaStatusPill({ status }) {
  const map = {
    connected:        "bg-emerald-50 text-emerald-700 ring-emerald-200",
    connecting:       "bg-blue-50 text-blue-700 ring-blue-200",
    action_required:  "bg-amber-50 text-amber-700 ring-amber-200",
    failed:           "bg-rose-50 text-rose-700 ring-rose-200",
    disconnected:     "bg-slate-100 text-slate-500 ring-slate-200",
    not_connected:    "bg-slate-100 text-slate-500 ring-slate-200",
  };
  const cls = map[status] ?? "bg-slate-100 text-slate-500 ring-slate-200";
  const labels = {
    connected: "Connected",
    connecting: "Connecting",
    action_required: "Action Required",
    failed: "Failed",
    disconnected: "Disconnected",
    not_connected: "Not Connected",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${cls}`}>
      {labels[status] ?? status}
    </span>
  );
}

const PROCESSING_STAGES = [
  { key: "authorization_received", label: "Authorization received" },
  { key: "token_exchange",         label: "Verifying token" },
  { key: "asset_discovery",        label: "Verifying WABA & phone number" },
  { key: "webhook_subscription",   label: "Setting up webhook" },
  { key: "phone_registration",     label: "Registering phone number" },
  { key: "connection_test",        label: "Running connection test" },
  { key: "complete",               label: "Complete" },
];

function WhatsAppStatusCard({ tenantId }) {
  const { accountStatus, loading, error, refetch } = useWhatsAppAccountStatus(tenantId);

  const iconFor = (status) => ({
    connected:       { bg: "bg-emerald-100", text: "text-emerald-600", symbol: "✓" },
    connecting:      { bg: "bg-blue-100",    text: "text-blue-600",    symbol: "↻" },
    action_required: { bg: "bg-amber-100",   text: "text-amber-600",   symbol: "⚠" },
    failed:          { bg: "bg-rose-100",    text: "text-rose-600",    symbol: "✕" },
    disconnected:    { bg: "bg-slate-100",   text: "text-slate-500",   symbol: "○" },
    not_connected:   { bg: "bg-slate-100",   text: "text-slate-400",   symbol: "○" },
  }[status] ?? { bg: "bg-slate-100", text: "text-slate-400", symbol: "?" });

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm xl:col-span-2">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* WhatsApp icon */}
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#25D366]/10">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-[#25D366]">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.001 22l4.978-1.306A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.078-1.117l-.292-.173-3.038.797.812-2.965-.19-.305A8 8 0 1112 20z"/>
            </svg>
          </span>
          <h3 className="text-base font-semibold text-slate-950">WhatsApp Account</h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={refetch}
            disabled={loading}
            title="Refresh Status"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Refresh Status"
          >
            <svg
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          {accountStatus?.status && <WaStatusPill status={accountStatus.status} />}
        </div>
      </div>

      {/* Body */}
      <div className="mt-5">
        {loading && (
          <div className="space-y-2 animate-pulse">
            <div className="h-3 w-2/5 rounded bg-slate-100" />
            <div className="h-3 w-3/5 rounded bg-slate-100" />
            <div className="h-3 w-1/3 rounded bg-slate-100" />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3">
            <span className="mt-0.5 text-rose-400">⚠</span>
            <p className="flex-1 text-sm text-rose-700">{error}</p>
            <button type="button" onClick={refetch} className="text-xs font-semibold text-rose-600 hover:underline">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && !accountStatus && (
          <p className="text-sm text-slate-400">No status available.</p>
        )}

        {!loading && !error && accountStatus && (() => {
          const { status, stage, account, checks, last_error } = accountStatus;
          const icon = iconFor(status);

          return (
            <div className="flex gap-4">
              {/* Status icon */}
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-base font-bold ${icon.bg} ${icon.text}`}>
                {status === "connecting" ? (
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : icon.symbol}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0 space-y-3">

                {/* not_connected / disconnected */}
                {(status === "not_connected" || status === "disconnected") && (
                  <p className="text-sm text-slate-500">
                    {status === "not_connected"
                      ? "No WhatsApp Business account is connected yet. Go to the Services tab to connect."
                      : "This WhatsApp account was disconnected. Go to the Services tab to reconnect."}
                  </p>
                )}

                {/* connecting */}
                {status === "connecting" && (() => {
                  const currentIdx = PROCESSING_STAGES.findIndex((s) => s.key === stage);
                  return (
                    <div>
                      <p className="text-sm font-medium text-blue-700">Verifying your WhatsApp account with Meta…</p>
                      <ol className="mt-4 space-y-2">
                        {PROCESSING_STAGES.map((s, idx) => {
                          const isDone = currentIdx > idx;
                          const isCurrent = currentIdx === idx;
                          const isPending = currentIdx < idx;
                          return (
                            <li key={s.key} className="flex items-center gap-2.5">
                              <span
                                className={[
                                  "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                                  isDone ? "bg-emerald-500" : "",
                                  isCurrent ? "ring-2 ring-blue-500 ring-offset-1 bg-blue-500" : "",
                                  isPending ? "border border-slate-300 bg-slate-50" : "",
                                ].join(" ")}
                              >
                                {isDone && (
                                  <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                                    <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                  </svg>
                                )}
                              </span>
                              <span
                                className={[
                                  "text-sm",
                                  isDone ? "text-slate-600" : "",
                                  isCurrent ? "text-blue-700 font-semibold" : "",
                                  isPending ? "text-slate-400" : "",
                                ].join(" ")}
                              >
                                {s.label}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  );
                })()}

                {/* connected — account + checks */}
                {status === "connected" && (
                  <>
                    <dl>
                      {account?.verified_name && (
                        <InfoRow label="Business name" value={account.verified_name} />
                      )}
                      {account?.display_phone_number && (
                        <InfoRow label="Phone number" value={account.display_phone_number} />
                      )}
                      {account?.waba_id && (
                        <InfoRow label="WABA ID" value={account.waba_id} />
                      )}
                      {account?.business_id && (
                        <InfoRow label="Business ID" value={account.business_id} />
                      )}
                      {account?.phone_number_id && (
                        <InfoRow label="Phone Number ID" value={account.phone_number_id} />
                      )}
                      {account?.connected_at && (
                        <InfoRow label="Connected" value={formatDate(account.connected_at)} />
                      )}
                    </dl>
                    {checks && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Health Checks</p>
                        {Object.entries(CHECK_LABELS).map(([key, label]) => (
                          <WaCheckRow key={key} label={label} value={checks[key]} />
                        ))}
                        {checks.last_health_check_at && (
                          <p className="mt-2 text-xs text-slate-400">
                            Last checked: {formatDate(checks.last_health_check_at)}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* action_required / failed */}
                {(status === "action_required" || status === "failed") && (
                  <>
                    <dl>
                      {account?.display_phone_number && (
                        <InfoRow label="Phone number" value={account.display_phone_number} />
                      )}
                      {account?.waba_id && (
                        <InfoRow label="WABA ID" value={account.waba_id} />
                      )}
                      {account?.business_id && (
                        <InfoRow label="Business ID" value={account.business_id} />
                      )}
                      {account?.phone_number_id && (
                        <InfoRow label="Phone Number ID" value={account.phone_number_id} />
                      )}
                    </dl>
                    {last_error && (
                      <div className={`rounded-xl border px-4 py-3 ${
                        status === "failed"
                          ? "border-rose-100 bg-rose-50"
                          : "border-amber-100 bg-amber-50"
                      }`}>
                        <p className={`text-xs font-semibold ${
                          status === "failed" ? "text-rose-700" : "text-amber-700"
                        }`}>
                          {last_error.code}
                        </p>
                        <p className={`mt-0.5 text-xs ${
                          status === "failed" ? "text-rose-600" : "text-amber-600"
                        }`}>
                          {last_error.message}
                        </p>
                        {last_error.recommended_action && (
                          <p className="mt-1 text-xs text-slate-500">
                            Recommended: <span className="font-medium capitalize">{last_error.recommended_action}</span>
                          </p>
                        )}
                      </div>
                    )}
                    {stage && (
                      <p className="text-xs text-slate-500 capitalize">Stage: {stage.replace(/_/g, " ")}</p>
                    )}
                    <p className="text-xs text-slate-400">Go to the Services tab to retry or disconnect.</p>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
}
// ─────────────────────────────────────────────────────────────────────────────


const TenantOverviewTab = ({ tenant, tenantId, onboardingSteps }) => {
  const metadata = tenant?.metadata_json || {};

  const resolvedSteps = ONBOARDING_STEPS.map((def) => {
    const live = (onboardingSteps || []).find((s) => s.step_key === def.key);
    return { ...def, status: live?.status || "pending" };
  });

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <WhatsAppStatusCard tenantId={tenantId} />
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-950">Tenant profile</h3>
          {tenantId ? (
            <a
              href={`/public/tenants/${tenantId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            >
              <svg className="h-3.5 w-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              View Public Profile
            </a>
          ) : null}
        </div>
        <dl className="mt-4">
          <InfoRow label="Name" value={tenant?.name} />
          <InfoRow label="Slug" value={tenant?.slug} />
          <InfoRow label="Industry" value={tenant?.industry} />
          <InfoRow label="Business type" value={tenant?.business_type} />
          <InfoRow label="Plan" value={tenant?.plan} />
          <InfoRow label="Status" value={<StatusBadge status={tenant?.status} />} />
        </dl>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-950">Owner information</h3>
        <dl className="mt-4">
          <InfoRow label="Owner name" value={tenant?.owner_name} />
          <InfoRow label="Owner email" value={tenant?.owner_email} />
          <InfoRow label="Owner phone" value={tenant?.owner_phone} />
          <InfoRow label="Country" value={tenant?.country} />
          <InfoRow label="Timezone" value={tenant?.timezone} />
        </dl>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-950">Tenant isolation</h3>
        <dl className="mt-4">
          <InfoRow label="Tenant ID" value={tenant?.id ? String(tenant.id) : "-"} />
          <InfoRow label="Legacy org ID" value={tenant?.legacy_org_id ? String(tenant.legacy_org_id) : "-"} />
        </dl>
        <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500 leading-relaxed">
          Each tenant operates as a fully isolated unit. The legacy org ID links to the previous single-org architecture for backward compatibility.
        </p>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-950">Onboarding readiness</h3>
        <p className="mt-1 text-xs text-slate-500">Step-by-step activation progress for this tenant.</p>
        <ol className="mt-5 space-y-3">
          {resolvedSteps.map((step, index) => {
            const isDone = step.status === "completed";
            const isInProgress = step.status === "in_progress";
            return (
              <li key={step.key} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-2 ${
                      isDone
                        ? "bg-emerald-500 text-white ring-emerald-200"
                        : isInProgress
                        ? "bg-[#096dea] text-white ring-blue-200"
                        : "bg-slate-100 text-slate-500 ring-slate-200"
                    }`}
                  >
                    {isDone ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </span>
                  {index < resolvedSteps.length - 1 ? (
                    <span className={`mt-1 h-full w-0.5 ${isDone ? "bg-emerald-300" : "bg-slate-200"}`} style={{ minHeight: "12px" }} />
                  ) : null}
                </div>
                <div className="pb-1 pt-0.5">
                  <p className={`text-sm font-semibold ${isDone ? "text-emerald-700" : isInProgress ? "text-[#096dea]" : "text-slate-700"}`}>
                    {step.label}
                    {step.required && !isDone ? <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-rose-500">Required</span> : null}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400 capitalize">{step.status.replace(/_/g, " ")}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm xl:col-span-2">
        <h3 className="text-base font-semibold text-slate-950">Metadata</h3>
        <pre className="mt-4 overflow-auto rounded-2xl bg-slate-950 p-4 text-sm text-slate-100">{JSON.stringify(metadata, null, 2)}</pre>
      </section>
    </div>
  );
};

export default TenantOverviewTab;
